/**
 * Per-number rate from the on-box app API.
 *
 * The API returns AUD rate/min direct from ASTPP for the *exact* dialed
 * number, so display == billing with no drift. Falls back to null (→ "—")
 * when the API says the destination isn't available; NEVER falls back to
 * the picker's cached AUD rate (that's what created the drift).
 *
 * Debounced by React Query's key: we only issue a request once the user has
 * typed at least MIN_DIGITS digits, so the pill isn't spamming during dialing.
 */
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { fetchAppApiRate, type AppApiRate } from "@/lib/app-api";

const MIN_DIGITS = 5; // country code (1-3) + at least 2-3 subscriber digits

export interface AppApiRateState {
  /** AUD rate per minute as a decimal. null while loading, unauth, unknown, or too-few digits. */
  rateAud: number | null;
  destination: string | null;
  available: boolean;
  isLoading: boolean;
  error: unknown;
}

/**
 * @param rawInput A phone number in any format ("+21192555000", "0021192555000", "21192555000").
 *                 We normalise to E.164 digits before hitting the API.
 */
export function useAppApiRate(rawInput: string | null | undefined): AppApiRateState {
  const digits = useMemo(() => normaliseToE164Digits(rawInput), [rawInput]);
  const enabled = !!digits && digits.length >= MIN_DIGITS;

  const { data, isLoading, error } = useQuery<AppApiRate | null>({
    queryKey: ["app-api-rate", digits],
    queryFn: async ({ signal }) => {
      const { data: sess } = await supabase.auth.getSession();
      if (!sess.session?.access_token) return null;
      try {
        return await fetchAppApiRate(digits!, signal);
      } catch (e) {
        // 4xx (unknown destination) is not an app error — surface as unavailable.
        const status = (e as { status?: number })?.status;
        if (status && status >= 400 && status < 500) return null;
        throw e;
      }
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    // Retry transient 5xx (box occasionally returns 502 under load). 4xx is
    // terminal — handled above as "unavailable".
    retry: (failureCount, err) => {
      const status = (err as { status?: number })?.status;
      if (status && status >= 400 && status < 500) return false;
      return failureCount < 2;
    },
    retryDelay: (attempt) => Math.min(500 * 2 ** attempt, 4000),
    refetchOnWindowFocus: false,
  });

  if (!enabled) {
    return { rateAud: null, destination: null, available: false, isLoading: false, error: null };
  }
  if (!data || !data.available || typeof data.rate_per_min !== "number") {
    return { rateAud: null, destination: data?.destination ?? null, available: false, isLoading, error };
  }
  return {
    rateAud: data.rate_per_min,
    destination: data.destination ?? null,
    available: true,
    isLoading,
    error,
  };
}

/**
 * Format an AUD /min rate. null → "—" (spec test #6: unknown number shows dash,
 * NEVER a hardcoded guess).
 */
export function formatAudRatePerMin(rateAud: number | null): string {
  if (rateAud == null || !Number.isFinite(rateAud)) return "—";
  return `A$${rateAud.toFixed(2)}/min`;
}

function normaliseToE164Digits(input: string | null | undefined): string | null {
  if (!input) return null;
  let s = input.trim().replace(/[^\d+]/g, "");
  if (s.startsWith("00")) s = "+" + s.slice(2);
  else if (s.startsWith("011")) s = "+" + s.slice(3);
  if (s.startsWith("+")) s = s.slice(1);
  if (!/^\d+$/.test(s)) return null;
  return s;
}
